'use client';

import { parseSegments } from './_utils/segments';
import { AuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import { CommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { FeatureFlagsProvider } from '@modules/feature-flags/provider/FeatureFlagsProvider';
import { installFullStoryLoggingStub } from '@modules/shared/analytics/scripts/fullstory-logging-stub';
import { markBundleEvaluationTime } from '@modules/shared/analytics/scripts/fullstoryUtils';
import { ZeusClientSetupProvider } from '@modules/shared/api/ZeusClientSetupProvider';
import { setLeaderRedirectUrlFromQuery } from '@utils/leaderRedirect';

import { StyleProvider, legacyLogicalPropertiesTransformer, legacyNotSelectorLinter } from '@ant-design/cssinjs';
import { RouterProvider, Toast } from '@capra/core';
import { App as AntdApp, ConfigProvider, ThemeConfig } from 'antd';
import '@utils/dayjs-setup';
import { usePathname, useRouter, useSearchParams, useSelectedLayoutSegments } from 'next/navigation';
import React, { Suspense } from 'react';

// Install the logging stub before any FS lifecycle events fire so dev
// debugging with FULLSTORY_LOGGING=true captures the full session timeline.
if (process.env.FULLSTORY_LOGGING === 'true' && process.env.FULLSTORY_ENABLED !== 'true') {
  installFullStoryLoggingStub();
}
markBundleEvaluationTime();

// It's a recreated theme from the legacy Ant Design v4 theme
const legacyAntdTheme: ThemeConfig = {
  token: {
    borderRadius: 2,
    colorPrimary: '#1890ff',
    wireframe: true,
    fontFamily: "'Open Sans', sans-serif",
  },
  components: {
    Menu: {
      itemBorderRadius: 0,
      subMenuItemBorderRadius: 0,
      itemHoverColor: '#1890ff',
      itemSelectedColor: '#1890ff',
      itemSelectedBg: '#e6f7ff',
      activeBarWidth: 3,
      itemMarginInline: 0,
      itemHoverBg: 'transparent',
      fontSize: 12,
    },
    Alert: {
      defaultPadding: '8px 15px',
    },
    Table: {
      headerSortActiveBg: '#f5f5f5',
      headerFilterHoverBg: 'rgba(0,0,0,.04)',
      headerSortHoverBg: '#f5f5f5',
    },
  },
  hashed: false,
};

interface ClientRootProps {
  children: React.ReactNode;
}

const LeaderRedirectHandler: React.FC = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const segments = parseSegments(useSelectedLayoutSegments() || []);
  const organizationId = segments.getIdByName('organizations');

  const from = searchParams?.get('from');

  React.useEffect(() => {
    if (from) {
      setLeaderRedirectUrlFromQuery();
    }
  }, [organizationId, from, pathname]);

  return null;
};

export const ClientRoot: React.FC<ClientRootProps> = ({ children }) => {
  const router = useRouter();

  return (
    <RouterProvider navigate={router.push}>
      <StyleProvider
        hashPriority='high'
        layer={false}
        linters={[legacyNotSelectorLinter]}
        transformers={[legacyLogicalPropertiesTransformer]}
      >
        <ConfigProvider theme={legacyAntdTheme}>
          <AntdApp>
            <Toast.Provider />
            <Suspense>
              <LeaderRedirectHandler />
              <AuthProvider>
                <CommonAuthProvider>
                  <ZeusClientSetupProvider>
                    <FeatureFlagsProvider>
                      {children}
                    </FeatureFlagsProvider>
                  </ZeusClientSetupProvider>
                </CommonAuthProvider>
              </AuthProvider>
            </Suspense>
          </AntdApp>
        </ConfigProvider>
      </StyleProvider>
    </RouterProvider>
  );
};
